import { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  CircularProgress,
  Alert,
  Chip,
  Avatar,
  Tooltip,
} from '@mui/material';
import {
  Create as CreateIcon,
  SwapHoriz,
  AssignmentInd,
  Chat,
  AttachFile,
  Draw,
  Email,
  Visibility,
  Circle,
} from '@mui/icons-material';
import correspondenceService from '../services/correspondenceService';
import { extractErrorMessage } from '../../../utils/errorUtils';

const typeConfig = {
  creation: { icon: <CreateIcon fontSize="small" />, color: '#1565c0', label: 'Creación' },
  state_change: { icon: <SwapHoriz fontSize="small" />, color: '#f57c00', label: 'Cambio de Estado' },
  assignment: { icon: <AssignmentInd fontSize="small" />, color: '#7b1fa2', label: 'Asignación' },
  thread: { icon: <Chat fontSize="small" />, color: '#2e7d32', label: 'Hilo' },
  document: { icon: <AttachFile fontSize="small" />, color: '#455a64', label: 'Documento' },
  signature: { icon: <Draw fontSize="small" />, color: '#c62828', label: 'Firma Electrónica' },
  email: { icon: <Email fontSize="small" />, color: '#00838f', label: 'Email' },
  read: { icon: <Visibility fontSize="small" />, color: '#6a1b9a', label: 'Lectura' },
};

const formatDate = (date) => {
  if (!date) return '—';
  return new Date(date).toLocaleString('es-CO', {
    year: 'numeric', month: '2-digit', day: '2-digit',
    hour: '2-digit', minute: '2-digit', second: '2-digit',
  });
};

const formatFileSize = (bytes) => {
  if (!bytes) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1048576) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / 1048576).toFixed(1)} MB`;
};

const EventDetails = ({ event }) => {
  const { type, details } = event;

  if (type === 'assignment' && details?.assignedTo) {
    return (
      <Box sx={{ mt: 0.5 }}>
        <Typography variant="body2" color="text.secondary">
          {details.assignedTo.email}
        </Typography>
        {details.read ? (
          <Chip label={`Leída: ${formatDate(details.readAt)}`} size="small" color="success" variant="outlined" sx={{ mt: 0.5 }} />
        ) : (
          <Chip label="No leída" size="small" color="warning" variant="outlined" sx={{ mt: 0.5 }} />
        )}
      </Box>
    );
  }

  if (type === 'thread' && details) {
    return (
      <Box sx={{ mt: 0.5 }}>
        {details.to && (
          <Typography variant="body2" color="text.secondary">
            Dirigido a: {details.to.name} ({details.to.email})
          </Typography>
        )}
        {details.taggedUsers?.length > 0 && (
          <Typography variant="body2" color="text.secondary">
            Etiquetados: {details.taggedUsers.length} usuario(s)
          </Typography>
        )}
        {details.message && (
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ mt: 0.5, fontStyle: 'italic', maxHeight: 60, overflow: 'hidden', textOverflow: 'ellipsis' }}
            dangerouslySetInnerHTML={{ __html: details.message }}
          />
        )}
        {details.deleted && <Chip label="Eliminado" size="small" color="error" variant="outlined" sx={{ mt: 0.5 }} />}
      </Box>
    );
  }

  if (type === 'document' && details) {
    return (
      <Box sx={{ mt: 0.5 }}>
        <Typography variant="body2" color="text.secondary">
          {details.medium === 'digital' ? 'Digital' : `Medio: ${details.medium}`}
          {details.fileSize ? ` — ${formatFileSize(details.fileSize)}` : ''}
        </Typography>
      </Box>
    );
  }

  if (type === 'email' && details) {
    return (
      <Box sx={{ mt: 0.5 }}>
        {details.subject && (
          <Typography variant="body2" color="text.secondary">Asunto: {details.subject}</Typography>
        )}
        <Typography variant="body2" color="text.secondary">
          Para: {details.recipientName || details.recipientEmail}
        </Typography>
        {details.opened ? (
          <Chip label={`Abierto: ${formatDate(details.openedAt)}`} size="small" color="success" variant="outlined" sx={{ mt: 0.5 }} />
        ) : (
          <Chip label="No abierto" size="small" variant="outlined" sx={{ mt: 0.5 }} />
        )}
      </Box>
    );
  }

  if (type === 'state_change' && details) {
    return (
      <Box sx={{ mt: 0.5, display: 'flex', alignItems: 'center', gap: 1 }}>
        <Chip label={details.from || '(ninguno)'} size="small" variant="outlined" />
        <Typography variant="body2" color="text.secondary">→</Typography>
        <Chip label={details.to} size="small" color="info" variant="outlined" />
      </Box>
    );
  }

  if (type === 'signature' && details) {
    return (
      <Box sx={{ mt: 0.5 }}>
        <Typography variant="body2" color="text.secondary" sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>
          Token: {details.signatureToken?.slice(0, 16)}...
        </Typography>
      </Box>
    );
  }

  return null;
};

const TimelineEvent = ({ event, isLast }) => {
  const config = typeConfig[event.type] || typeConfig.creation;

  return (
    <Box sx={{ display: 'flex', gap: 2, position: 'relative' }}>
      {/* Timeline line */}
      {!isLast && (
        <Box sx={{
          position: 'absolute', left: 19, top: 40, bottom: -8,
          width: 2, bgcolor: 'divider',
        }} />
      )}

      {/* Icon */}
      <Tooltip title={config.label}>
        <Avatar sx={{ width: 38, height: 38, bgcolor: config.color, flexShrink: 0 }}>
          {config.icon}
        </Avatar>
      </Tooltip>

      {/* Content */}
      <Paper variant="outlined" sx={{ flex: 1, p: 2, mb: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="subtitle2">{event.description}</Typography>
            <Chip label={config.label} size="small" sx={{ bgcolor: config.color, color: '#fff', height: 20, fontSize: '0.7rem' }} />
          </Box>
          <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'monospace' }}>
            {formatDate(event.date)}
          </Typography>
        </Box>

        {event.actor && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5 }}>
            <Circle sx={{ fontSize: 6, color: 'text.disabled' }} />
            <Typography variant="body2" color="text.secondary">
              {event.actor.name}{event.actor.email ? ` (${event.actor.email})` : ''}
            </Typography>
          </Box>
        )}

        <EventDetails event={event} />
      </Paper>
    </Box>
  );
};

const CorrespondenceTraceability = ({ correspondenceId }) => {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const load = async () => {
      try {
        setLoading(true);
        const res = await correspondenceService.getTraceability(correspondenceId);
        setEvents(res.data || []);
      } catch (err) {
        setError(extractErrorMessage(err));
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [correspondenceId]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return <Alert severity="error">{error}</Alert>;
  }

  if (events.length === 0) {
    return <Alert severity="info">No se encontraron eventos de trazabilidad.</Alert>;
  }

  // Group events by date
  const grouped = {};
  for (const event of events) {
    const dateKey = new Date(event.date).toLocaleDateString('es-CO', {
      weekday: 'long', year: 'numeric', month: 'long', day: 'numeric',
    });
    if (!grouped[dateKey]) grouped[dateKey] = [];
    grouped[dateKey].push(event);
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
        <Typography variant="h5" component="h2">Trazabilidad</Typography>
        <Chip label={`${events.length} eventos`} size="small" variant="outlined" />
      </Box>

      {Object.entries(grouped).map(([dateLabel, dayEvents]) => (
        <Box key={dateLabel} sx={{ mb: 3 }}>
          <Typography variant="overline" color="text.secondary" sx={{ mb: 1, display: 'block', fontWeight: 600 }}>
            {dateLabel}
          </Typography>
          {dayEvents.map((event, i) => (
            <TimelineEvent key={`${event.type}-${event.date}-${i}`} event={event} isLast={i === dayEvents.length - 1} />
          ))}
        </Box>
      ))}
    </Box>
  );
};

export default CorrespondenceTraceability;
